import { ColumnDetails, ColumnType } from '../../../../../../../../grid/types/common';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { html, TemplateResult } from 'lit';
import { USER_GROUPS_TYPE } from '../../../../../utils/uiSettings/exchange/exchange-protection.types';
import { getGroupTypeValues } from '../../../../../utils/uiSettings/exchange/exchange-protection.values';

export enum Column {
  NAME = 0,
  COMPANY = 1,
  GROUP_TYPE = 2,
  DESCRIPTION = 3,
  USERS = 4,
}

export enum ColumnKeys {
  NAME = 'name',
  DESCRIPTION = 'specifics.description',
  COMPANY = 'specifics.company.name',
  GROUP_TYPE = 'type',
  USERS = 'emails',
}

export function getGridColumns(): Column[] {
  if (!Application.Main.product.isCloud()) {
    return [Column.NAME, Column.DESCRIPTION, Column.GROUP_TYPE, Column.USERS];
  }
  if (Application.Main.company.isCustomer()) {
    return [Column.NAME, Column.DESCRIPTION, Column.USERS];
  }
  // cloud and partner
  return [Column.NAME, Column.COMPANY, Column.DESCRIPTION, Column.USERS];
}

export function getPredefinedFilterKeys(): ColumnKeys[] {
  if (!Application.Main.product.isCloud()) {
    return [ColumnKeys.NAME, ColumnKeys.DESCRIPTION, ColumnKeys.GROUP_TYPE];
  }
  if (Application.Main.company.isCustomer()) {
    return [ColumnKeys.NAME, ColumnKeys.DESCRIPTION];
  }

  // cloud and partner
  return [ColumnKeys.COMPANY, ColumnKeys.NAME, ColumnKeys.DESCRIPTION];
}

export function getFilters() {
  if (!Application.Main.product.isCloud()) {
    return [
      { [ColumnKeys.NAME]: { contains: '' } },
      { [ColumnKeys.DESCRIPTION]: { contains: '' } },
      { [ColumnKeys.GROUP_TYPE]: { in: [] } },
    ];
  }

  if (Application.Main.company.isCustomer()) {
    return [{ [ColumnKeys.NAME]: { contains: '' } }, { [ColumnKeys.DESCRIPTION]: { contains: '' } }];
  }

  // cloud and partner
  return [
    { [ColumnKeys.COMPANY]: { in: [] } },
    { [ColumnKeys.NAME]: { contains: '' } },
    { [ColumnKeys.DESCRIPTION]: { contains: '' } },
  ];
}

export function getGridColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
  const base = [
    {
      column: Column.NAME,
      key: ColumnKeys.NAME,
      columnName: translateFn('Group name'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
    },
    {
      column: Column.DESCRIPTION,
      key: ColumnKeys.DESCRIPTION,
      columnName: translateFn('Description'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
    },
    {
      column: Column.USERS,
      key: ColumnKeys.USERS,
      columnName: translateFn('Email addresses'),
      type: ColumnType.STRING,
      removable: false,
      filteringDisabled: true,
      sortable: false,
    },
  ];

  if (!Application.Main.product.isCloud()) {
    return [
      ...base,
      {
        column: Column.GROUP_TYPE,
        key: ColumnKeys.GROUP_TYPE,
        columnName: translateFn('Group type'),
        type: ColumnType.ENUM,
        removable: false,
        values: getGroupTypeValues(),
        sortable: true,
      },
    ];
  }

  if (Application.Main.company.isCustomer()) {
    return base;
  }
  // cloud and partner
  return [
    ...base,
    {
      column: Column.COMPANY,
      key: ColumnKeys.COMPANY,
      columnName: translateFn('Company'),
      type: ColumnType.COMPANY,
      enumFilterCanSelectAll: true,
      showOnlySelectAll: true,
      removable: false,
      sortable: true,
    },
  ];
}

export function companyRenderer(rowData: any, columnDetails: ColumnDetails<Column, ColumnKeys>): TemplateResult {
  return html`<div>${rowData.item['specifics']['company']['name']}</div>`;
}
export function descriptionRenderer(rowData: any, columnDetails: ColumnDetails<Column, ColumnKeys>): TemplateResult {
  return html`<div>${rowData.item['specifics']['description']}</div>`;
}
export function nameRenderer(rowData: any, columnDetails: ColumnDetails<Column, ColumnKeys>): TemplateResult {
  if (rowData.item['type'] === USER_GROUPS_TYPE.CUSTOM) {
    return html`<div
      class="text-medium"
      style="cursor: pointer; color: var(--blue-layout-400-primary); "
      @click="${() => this.onEditRow(rowData)}"
    >
      ${rowData.item['name']}
    </div>`;
  }
  return html`<div class="text-medium">${rowData.item['name']}</div>`;
}
